'use client'

import { getCurrentUser } from '@/actions/user'
import { useCallback, useEffect, useState } from 'react'

/**
 * Custom hook to fetch current authenticated user data using server actions
 * @param {object} options - Query options
 * @param {object} options.select - Fields to select
 * @param {object} options.include - Relations to include
 * @param {boolean} options.enabled - Whether to fetch data (default: true)
 * @returns {object} - { data, isLoading, error, refresh }
 */
export function useCurrentUser(options = {}) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { enabled = true, ...queryOptions } = options

  const queryKey = JSON.stringify(queryOptions)

  const fetchUser = useCallback(async () => {
    if (!enabled) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getCurrentUser(JSON.parse(queryKey))
      setUser(data)
    } catch (err) {
      setError(err)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [enabled, queryKey])

  useEffect(() => {
    fetchUser()
  }, [fetchUser])

  const refresh = useCallback(() => {
    return fetchUser()
  }, [fetchUser])

  return {
    data: user,
    isLoading: loading,
    error,
    refresh,
  }
}
